import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import OptionBox from '@app/src/components/Ui/OptionBox'
import { ProvidersModalPurpose, useProvidersModal } from '@app/src/context/ProvidersModalContext'
import { RequestAutomationType } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/RequestAutomationType'
import RequestAutomationWarningAlert from '@app/src/pages/Configurations/ConfigurationsPages/Automation/Warning/RequestAutomationWarningAlert'
import ImportProvidersModal from '@app/src/pages/ResourceCollection/Collections/Provider/ConnectProviders/ImportProvidersModal'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Provider } from '@app/src/types/organizations'
import AddBoxOutlined from '@mui/icons-material/AddBoxOutlined'
import AddLinkIcon from '@mui/icons-material/AddLink'
import MailOutline from '@mui/icons-material/MailOutline'
import SearchIcon from '@mui/icons-material/Search'
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined'
import { Stack, useTheme } from '@mui/material'
import { capitalize } from 'lodash'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useDrawer } from '../DrawerContext'
import DrawerView, { DrawerViewProps } from '../DrawerView'
import DrawerViewAddProviderPlaceholder from './DrawerViewAddProviderPlaceholder'
import DrawerViewAddProviderReferral from './DrawerViewAddProviderReferral'
import DrawerViewFindProvider from './DrawerViewFindProvider'

type DrawerViewAddProviderProps = {
  providerType: string
} & Omit<DrawerViewProps, 'title'>

const DrawerViewAddProvider: React.FC<DrawerViewAddProviderProps> = ({ providerType, ...props }) => {
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()
  const theme = useTheme()
  const { handleOpenConnectDialog } = useProvidersModal()
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)

  const iconBgColor = theme.palette.secondary.light

  const { items: providersWithoutInvite, isLoading } = useFetchCollectionWithPost<Provider>({
    key: [FetchKey.ProviderCollection],
    endpoint: endpoints.providersCollection,
    payload: {
      filter: [
        {
          name: 'referralContact',
          filters: [{ operator: Operators.IsNotNull }],
        },
        {
          name: 'linked',
          filters: [{ operator: Operators.EqualTo, value: false }],
        },
      ],
      include: ['referralContact', 'organization.contacts'],
    },
  })

  const uninvitedCount = providersWithoutInvite.length

  const handleOpenImportModal = () => {
    setIsImportModalOpen(true)
  }

  return (
    <>
      <DrawerView
        title={formatMessage(
          { id: 'resourceCollections.create.addProvider' },
          { provider: formatMessage({ id: `general.${capitalize(providerType)}` }, { count: 1 }).toLowerCase() },
        )}
        {...props}
      >
        <Stack>
          <OptionBox
            title={formatMessage({ id: 'resourceCollections.create.findProviderTitle' })}
            description={formatMessage({ id: 'resourceCollections.create.findProviderDescription' })}
            onClick={() => openDrawer(<DrawerViewFindProvider providerType={providerType} />)}
            iconComponent={SearchIcon}
            iconBackgroundColor={iconBgColor}
            data-testid="find-suppliers"
          />
          <OptionBox
            title={formatMessage({ id: 'resourceCollections.create.invitation' })}
            description={formatMessage({ id: 'resourceCollections.create.invitationDescription' })}
            onClick={() => openDrawer(<DrawerViewAddProviderReferral providerType={providerType} />)}
            iconComponent={AddLinkIcon}
            iconBackgroundColor={iconBgColor}
            data-testid="send-invitation-link"
          />
          <OptionBox
            title={formatMessage({ id: 'resourceCollections.create.bulkInvite' }, { count: uninvitedCount })}
            description={formatMessage({ id: 'resourceCollections.create.bulkInviteDescription' })}
            onClick={() =>
              !isLoading && handleOpenConnectDialog(providersWithoutInvite, ProvidersModalPurpose.InitialInvitation)
            }
            iconComponent={MailOutline}
            iconBackgroundColor={iconBgColor}
            data-testid="bulk-invite-companies"
            disabled={isLoading || uninvitedCount === 0}
            loading={isLoading}
          />
          <OptionBox
            title={formatMessage({ id: 'resourceCollections.create.providerExcel' }, { provider: providerType })}
            description={formatMessage(
              { id: 'resourceCollections.create.providerExcelDescription' },
              { provider: providerType },
            )}
            onClick={handleOpenImportModal}
            iconComponent={UploadFileOutlined}
            iconBackgroundColor={theme.palette.grey[200]}
            data-testid="import-providers"
          />
          <OptionBox
            title={formatMessage(
              { id: 'resourceCollections.create.createProviderPlaceholder' },
              { provider: formatMessage({ id: `general.${capitalize(providerType)}` }, { count: 1 }).toLowerCase() },
            )}
            description={formatMessage({ id: 'resourceCollections.create.createProviderPlaceholderDescription' })}
            onClick={() => openDrawer(<DrawerViewAddProviderPlaceholder providerType={providerType} />)}
            iconComponent={AddBoxOutlined}
            iconBackgroundColor={theme.palette.grey[200]}
            data-testid="create-supplier-link-later"
          />
        </Stack>
        <Stack p={2}>
          <RequestAutomationWarningAlert
            automationType={RequestAutomationType.FOR_EVERY_NEW_PROVIDER}
            providerType={providerType}
          />
        </Stack>
      </DrawerView>
      <ImportProvidersModal open={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} />
    </>
  )
}

export default DrawerViewAddProvider
